/**
 * The four questions this product exists to answer, in one place:
 * what is missing, what needs confirming, what conflicts, and what to ask next.
 */

import { useState } from "react";

import { knowledge as knowledgeApi, projects as projectsApi } from "../api/resources";
import {
  Card,
  EmptyState,
  ErrorBanner,
  Field,
  Loading,
  Modal,
  PageHeader,
  Stat,
  StatusBadge,
  Tabs,
  Triple,
} from "../components/ui";
import { AssistantPanel } from "../components/assistant/AssistantPanel";
import { useCurrentOrganization } from "../lib/OrganizationContext";
import { confidence, predicateLabel, relativeTime } from "../lib/format";
import { useAsync } from "../lib/useAsync";
import type { Contradiction } from "../lib/types";

type Tab = "gaps" | "contradictions" | "questions";

const RESOLUTIONS = [
  { value: "fact_a", label: "The first statement is correct" },
  { value: "fact_b", label: "The second statement is correct" },
  { value: "both_true", label: "Both are true (different scopes)" },
  { value: "new_value", label: "Neither — the correct value is…" },
  { value: "neither", label: "Neither is correct" },
];

export function GapsPage() {
  const { organizationId, project } = useCurrentOrganization();
  const [tab, setTab] = useState<Tab>("gaps");
  const [askOpen, setAskOpen] = useState(false);
  const [actor, setActor] = useState(() => localStorage.getItem("discovery.actor") ?? "");
  const [resolving, setResolving] = useState<Contradiction | undefined>();
  const [resolution, setResolution] = useState("fact_a");
  const [newValue, setNewValue] = useState("");
  const [note, setNote] = useState("");
  const [waiving, setWaiving] = useState<string | undefined>();
  const [waiveNote, setWaiveNote] = useState("");
  const [error, setError] = useState<string | undefined>();

  const gaps = useAsync(
    () =>
      organizationId && project
        ? projectsApi.gaps(organizationId, project.id)
        : Promise.resolve([]),
    [organizationId, project?.id],
  );
  const contradictions = useAsync(
    () => (organizationId ? knowledgeApi.contradictions(organizationId) : Promise.resolve([])),
    [organizationId],
  );
  const followUps = useAsync(
    () =>
      organizationId && project
        ? projectsApi.followUps(organizationId, project.id, "pending")
        : Promise.resolve([]),
    [organizationId, project?.id],
  );

  if (!organizationId) {
    return (
      <Card>
        <EmptyState title="No organization selected" />
      </Card>
    );
  }
  if (!project) {
    return (
      <Card>
        <EmptyState
          title="No onboarding project"
          description="Gaps and follow-up questions are tracked per onboarding project."
        />
      </Card>
    );
  }

  const openGaps = (gaps.data ?? []).filter((row) => row.status === "open");
  const openContradictions = (contradictions.data ?? []).filter((row) => row.status === "open");
  const requiredGaps = openGaps.filter((row) => row.severity === "required");

  async function resolveContradiction() {
    if (!resolving) return;
    setError(undefined);
    try {
      if (resolution === "both_true") {
        await knowledgeApi.dismissContradiction(organizationId!, resolving.id, {
          actor: actor || undefined,
          reason: note || undefined,
        });
      } else {
        await knowledgeApi.resolveContradiction(organizationId!, resolving.id, {
          resolution,
          note: note || undefined,
          new_value: resolution === "new_value" ? newValue : undefined,
          actor: actor || undefined,
        });
      }
      setResolving(undefined);
      setNote("");
      setNewValue("");
      contradictions.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  async function waiveGap() {
    if (!waiving) return;
    setError(undefined);
    try {
      await projectsApi.waiveGap(organizationId!, project!.id, waiving, {
        note: waiveNote,
        actor: actor || undefined,
      });
      setWaiving(undefined);
      setWaiveNote("");
      gaps.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  return (
    <>
      <PageHeader
        title="Gaps & contradictions"
        description="What we still do not know, what two people disagree about, and what to ask next. Unknown stays unknown until someone answers."
        actions={
          <>
          <button type="button" className="btn" onClick={() => setAskOpen((open) => !open)}>
            {askOpen ? "Hide assistant" : "Explain a conflict"}
          </button>
          <input
            style={{ width: 190 }}
            placeholder="Your name (for the audit trail)"
            value={actor}
            onChange={(event) => {
              setActor(event.target.value);
              localStorage.setItem("discovery.actor", event.target.value);
            }}
          />
          </>
        }
      />

      {askOpen && organizationId ? (
        <div style={{ height: 420 }}>
          <AssistantPanel
            organizationId={organizationId}
            mode="conflict_assistant"
            operation="contradiction_analysis"
            title="Assistant — about a conflict"
            suggestions={[
              "What are the open contradictions?",
              "What does each position rest on?",
              "What question would settle this?",
            ]}
            onClose={() => setAskOpen(false)}
          />
        </div>
      ) : null}

      <div className="stack">
        <ErrorBanner error={gaps.error ?? contradictions.error ?? error} />

        <div className="grid grid--stats">
          <Stat label="Required gaps" value={requiredGaps.length} tone={requiredGaps.length ? "danger" : "ok"} />
          <Stat label="All open gaps" value={openGaps.length} />
          <Stat
            label="Open contradictions"
            value={openContradictions.length}
            tone={openContradictions.length ? "danger" : "ok"}
          />
          <Stat label="Open questions" value={followUps.data?.length ?? 0} />
        </div>

        <Tabs
          value={tab}
          onChange={setTab}
          options={[
            { value: "gaps", label: "Missing knowledge", count: openGaps.length },
            { value: "contradictions", label: "Contradictions", count: openContradictions.length },
            { value: "questions", label: "Open questions", count: followUps.data?.length ?? 0 },
          ]}
        />

        {tab === "gaps" ? (
          <Card flush>
            {gaps.loading ? (
              <Loading />
            ) : openGaps.length === 0 ? (
              <EmptyState
                title="No open gaps"
                description="Every expectation declared by the selected modules has been answered or waived."
              />
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>What is missing</th>
                      <th>Subject</th>
                      <th>Module</th>
                      <th>Severity</th>
                      <th>Ask</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {openGaps.map((gap) => (
                      <tr key={gap.id}>
                        <td>{gap.title}</td>
                        <td className="small muted">
                          {gap.subject_name ?? gap.subject_type ?? "organization"}
                        </td>
                        <td className="small muted">{gap.module_key}</td>
                        <td>
                          <StatusBadge status={gap.severity} />
                        </td>
                        <td className="mono small">{gap.suggested_question_semantic_id ?? "—"}</td>
                        <td>
                          <button className="btn--small" onClick={() => setWaiving(gap.id)}>
                            Waive
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        ) : null}

        {tab === "contradictions" ? (
          <div className="stack">
            {contradictions.loading ? (
              <Loading />
            ) : (contradictions.data ?? []).length === 0 ? (
              <Card>
                <EmptyState
                  title="No contradictions detected"
                  description="Two facts contradict when they fill the same single-valued slot with different values. Multi-valued predicates never contradict."
                />
              </Card>
            ) : (
              (contradictions.data ?? []).map((row) => (
                <Card
                  key={row.id}
                  title={`${row.subject_name} — ${predicateLabel(row.predicate)}`}
                  actions={
                    <div className="row">
                      <StatusBadge status={row.status} />
                      {row.status === "open" ? (
                        <button
                          className="primary"
                          onClick={() => {
                            setResolving(row);
                            setResolution("fact_a");
                          }}
                        >
                          Resolve
                        </button>
                      ) : null}
                    </div>
                  }
                >
                  <div className="grid grid--two">
                    {[row.fact_a, row.fact_b].map((fact, index) =>
                      fact ? (
                        <div
                          key={fact.id}
                          style={{
                            border: "1px solid var(--border)",
                            borderRadius: 6,
                            padding: 12,
                          }}
                        >
                          <div className="tiny faint">Statement {index === 0 ? "A" : "B"}</div>
                          <Triple
                            subject={fact.subject_name}
                            predicate={predicateLabel(fact.predicate)}
                            object={fact.object_value}
                          />
                          <div className="evidence" style={{ marginTop: 8 }}>
                            <div className="evidence__ref">{fact.evidence_reference}</div>
                            <div className="tiny faint" style={{ marginTop: 4 }}>
                              {fact.source_type.replace(/_/g, " ")} · confidence{" "}
                              {confidence(fact.confidence)} · {relativeTime(fact.created_at)}
                            </div>
                          </div>
                        </div>
                      ) : null,
                    )}
                  </div>
                  {row.resolution_note ? (
                    <p className="small muted" style={{ marginBottom: 0 }}>
                      Resolution: {row.resolution} — {row.resolution_note}
                    </p>
                  ) : null}
                </Card>
              ))
            )}
          </div>
        ) : null}

        {tab === "questions" ? (
          <Card flush>
            {followUps.loading ? (
              <Loading />
            ) : (followUps.data ?? []).length === 0 ? (
              <EmptyState
                title="No open questions"
                description="Follow-up rules raise questions when answers, policies or gaps call for them."
              />
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Question</th>
                      <th>Why it was asked</th>
                      <th>Rule</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {(followUps.data ?? []).map((row) => (
                      <tr key={row.id}>
                        <td>
                          {row.question_text}
                          <div className="tiny mono faint">{row.semantic_id}</div>
                        </td>
                        <td className="small muted">{row.reason}</td>
                        <td className="small mono">{row.rule_key}</td>
                        <td>
                          <button
                            className="btn--small"
                            onClick={async () => {
                              await projectsApi.dismissFollowUp(organizationId!, project!.id, row.id);
                              followUps.reload();
                            }}
                          >
                            Dismiss
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        ) : null}
      </div>

      {resolving ? (
        <Modal
          title="Resolve contradiction"
          onClose={() => setResolving(undefined)}
          footer={
            <>
              <button onClick={() => setResolving(undefined)}>Cancel</button>
              <button className="primary" onClick={() => void resolveContradiction()}>
                Record decision
              </button>
            </>
          }
        >
          <p className="small muted">
            The system does not choose. Recording a decision verifies the winner and supersedes the
            other — both keep their evidence.
          </p>
          <Field label="Decision">
            <select value={resolution} onChange={(event) => setResolution(event.target.value)}>
              {RESOLUTIONS.map((row) => (
                <option key={row.value} value={row.value}>
                  {row.label}
                </option>
              ))}
            </select>
          </Field>
          {resolution === "new_value" ? (
            <Field label="Correct value">
              <input value={newValue} onChange={(event) => setNewValue(event.target.value)} />
            </Field>
          ) : null}
          <Field label="Note" hint="Recorded in the contradiction audit trail">
            <input value={note} onChange={(event) => setNote(event.target.value)} />
          </Field>
        </Modal>
      ) : null}

      {waiving ? (
        <Modal
          title="Waive knowledge gap"
          onClose={() => setWaiving(undefined)}
          footer={
            <>
              <button onClick={() => setWaiving(undefined)}>Cancel</button>
              <button
                className="primary"
                disabled={waiveNote.trim().length === 0}
                onClick={() => void waiveGap()}
              >
                Waive
              </button>
            </>
          }
        >
          <p className="small muted">
            Waiving records that this knowledge does not apply to this company. It is not the same
            as answering, and the note is required.
          </p>
          <Field label="Why does this not apply?">
            <input value={waiveNote} onChange={(event) => setWaiveNote(event.target.value)} />
          </Field>
        </Modal>
      ) : null}
    </>
  );
}
